import moment from "moment";
import { EAccessLevel } from "../enums/access-level";
import { EAccessScope } from "../enums/access-scope";
import { EOrgUserRole } from "../enums/org-user-role";
import { EOrgUserStatus } from "../enums/org-user-status";
import { UserRole } from "../enums/user-roles";
import { IAccessGrant } from "../interfaces/access-grant";

export const orgUserRoleLabels: Record<EOrgUserRole, string> = {
  [EOrgUserRole.ROLE_ORG_ADMIN]: "Organization admin",
  [EOrgUserRole.ROLE_ORG_VIEWER]: "Organization viewer",
  [EOrgUserRole.ROLE_ORG_OPERATOR]: "Site manager",
  [EOrgUserRole.ROLE_ORG_GUEST]: "Monitoring guest",
};

// Labels any role current/access can return, including the platform admin's.
export const getRoleLabel = (role?: string) => {
  if (role === UserRole.ROLE_ADMIN) return "Platform admin";
  return orgUserRoleLabels[role as EOrgUserRole] ?? "";
};

// Users and Settings are for organization and platform admins; every other role gets a
// 401 from the user endpoints. platformAdmin is the authoritative platform-wide flag, so it
// is honoured whatever role accompanies it.
export const canManageUsers = (access?: {
  role?: string;
  platformAdmin?: boolean;
}) =>
  !!access &&
  (access.platformAdmin === true ||
    access.role === EOrgUserRole.ROLE_ORG_ADMIN ||
    access.role === UserRole.ROLE_ADMIN);

export const getOrgUserStatusInfo = (
  status: EOrgUserStatus,
): { label: string; color: string } => {
  switch (status) {
    case EOrgUserStatus.ACTIVE:
      return { label: "Active", color: "#29cc39" };
    case EOrgUserStatus.INACTIVE:
      return { label: "Inactive", color: "#e45e5e" };
    case EOrgUserStatus.EXPIRED:
      return { label: "Expired", color: "#f29d38" };
    case EOrgUserStatus.NO_ACCESS:
      return { label: "No access", color: "#e45e5e" };
    case EOrgUserStatus.INVITED:
      return { label: "Invited", color: "#a3a8b4" };
    default:
      return { label: status, color: "#a3a8b4" };
  }
};

export const getAccessLevelInfo = (
  level: EAccessLevel,
): { label: string; color: string; bgColor: string } => {
  switch (level) {
    case EAccessLevel.ADMIN:
      return { label: "Admin", color: "#2f2a89", bgColor: "#ecebfb" };
    case EAccessLevel.VIEW:
      return { label: "View", color: "#383941", bgColor: "#f0f1f4" };
    case EAccessLevel.OPERATE:
      return { label: "Operate", color: "#0e7a1a", bgColor: "#e3f6e5" };
    case EAccessLevel.VIEW_REDACTED:
      return { label: "View redacted", color: "#a5610b", bgColor: "#fdf0d9" };
    default:
      return { label: "No access", color: "#bf4545", bgColor: "#faecec" };
  }
};

export const accessLevelDescriptions: Record<EAccessLevel, string> = {
  [EAccessLevel.ADMIN]:
    "Everything, including users and structure below this point",
  [EAccessLevel.OPERATE]: "View and act: control devices, flag customers",
  [EAccessLevel.VIEW]: "Read-only, full values",
  [EAccessLevel.VIEW_REDACTED]:
    "Read-only, sensitive values hidden. Must expire",
  [EAccessLevel.NO_ACCESS]: "Sees nothing",
};

// Strongest first; No access is the absence of a grant, so it is never granted.
export const grantableAccessLevels = [
  EAccessLevel.ADMIN,
  EAccessLevel.OPERATE,
  EAccessLevel.VIEW,
  EAccessLevel.VIEW_REDACTED,
];

// A Monitoring guest can only hold View redacted access.
export const getAllowedAccessLevels = (role?: EOrgUserRole) =>
  role === EOrgUserRole.ROLE_ORG_GUEST
    ? [EAccessLevel.VIEW_REDACTED]
    : grantableAccessLevels;

// A null max means the pool is unlimited.
export const isSeatPoolFull = (
  max: number | null | undefined,
  occupied: number,
) => max !== null && max !== undefined && occupied >= max;

export const takesGuestSeat = (grants: IAccessGrant[]) =>
  grants.length > 0 &&
  grants.every((g) => g.level === EAccessLevel.VIEW_REDACTED);

// View redacted is for temporary guests, so it can never be open-ended.
export const isExpiryRequired = (level: EAccessLevel) =>
  level === EAccessLevel.VIEW_REDACTED;

// expiresAt is exclusive: access through 31 Dec is sent as 1 Jan 00:00. The UI shows and
// picks the last day of access instead.
export const getLastAccessDay = (expiresAt: string) =>
  moment(expiresAt).subtract(1, "millisecond");

export const toExclusiveExpiry = (lastAccessDay: Date) =>
  moment(lastAccessDay).startOf("day").add(1, "day").toISOString();

export const formatLastAccessDay = (expiresAt: string) =>
  getLastAccessDay(expiresAt).format("D MMM YYYY");

export const formatGrantExpiry = (expiresAt?: string) =>
  expiresAt ? `Expires ${formatLastAccessDay(expiresAt)}` : "Never expires";

export const accessScopeLabels: Record<EAccessScope, string> = {
  [EAccessScope.ORGANIZATION]: "Organization",
  [EAccessScope.BRANCH]: "Branch",
  [EAccessScope.SITE]: "Site",
};

export const isGrantExpired = (grant: IAccessGrant) =>
  !!grant.expiresAt && !moment().isBefore(grant.expiresAt);

// Active grants first, strongest level first, so a user's first grant is the access they
// actually have.
export const sortGrantsByStrength = (grants: IAccessGrant[]) =>
  [...grants].sort(
    (a, b) =>
      Number(isGrantExpired(a) || !!a.isMissing) -
        Number(isGrantExpired(b) || !!b.isMissing) ||
      grantableAccessLevels.indexOf(a.level) -
        grantableAccessLevels.indexOf(b.level),
  );

// The line under a grant: expiry wins over the scope, since an expiring grant
// matters more to an admin than where it applies.
export const getGrantDescription = (grant: IAccessGrant) => {
  if (grant.level === EAccessLevel.NO_ACCESS) {
    return "Sees nothing until access is added";
  }

  if (grant.isMissing) {
    return "Site no longer exists";
  }

  if (grant.expiresAt) {
    const date = formatLastAccessDay(grant.expiresAt);
    return isGrantExpired(grant) ? `Expired ${date}` : `Expires ${date}`;
  }

  switch (grant.scope) {
    case EAccessScope.ORGANIZATION:
      return "Whole organization";
    case EAccessScope.BRANCH:
      return "Branch and every site under it";
    default:
      return "One site";
  }
};

// Finds a grant that overrides a broader one, to show the admin how overlapping
// grants resolve, e.g. "Site 83 is Admin even though the organization grant is View".
export const getOverlapExample = (grants: IAccessGrant[]) => {
  const effectiveGrants = grants.filter(
    (g) => !g.isMissing && !isGrantExpired(g),
  );
  // Sites first: they are the most specific, so they make the clearest example.
  const mostSpecificFirst = [
    ...effectiveGrants.filter((g) => g.scope === EAccessScope.SITE),
    ...effectiveGrants.filter((g) => g.scope === EAccessScope.BRANCH),
  ];

  for (const grant of mostSpecificFirst) {
    // The closest ancestor wins, so look from the nearest one up.
    const ancestor = [...grant.ancestorIds]
      .reverse()
      .map((id) => effectiveGrants.find((g) => g.identity === id))
      .find((a): a is IAccessGrant => !!a);

    if (ancestor && ancestor.level !== grant.level) {
      return `${grant.scopeName} is ${
        getAccessLevelInfo(grant.level).label
      } even though the ${accessScopeLabels[
        ancestor.scope
      ].toLowerCase()} grant is ${getAccessLevelInfo(ancestor.level).label}`;
    }
  }

  return undefined;
};
